import React, { useState, useEffect, useRef, useCallback } from 'react';
import { projectsAPI, usersAPI, importAPI } from '../services/api';
import useAuthStore from '../stores/authStore';
import { Plus, Edit2, Trash2, X, Search, FolderPlus, UserPlus } from 'lucide-react';

const Projects = () => {
  const { user, isAdmin, isManager } = useAuthStore();
  const [projects, setProjects] = useState([]);
  const [managers, setManagers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const observer = useRef();
  const lastElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prev => prev + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    size: 10,
    total_records: 0,
    total_pages: 1
  });
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', client: '', client_id: '', manager_ids: [], reporting_area: '', description: '', icon: null });
  const [selectedProject, setSelectedProject] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [modalMembers, setModalMembers] = useState([]);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hoveredProject, setHoveredProject] = useState(null);
  const [blurbPos, setBlurbPos] = useState({ x: 0, y: 0 });
  const [detailProject, setDetailProject] = useState(null); // for detail drawer

  // Member search combobox state
  const [memberSearchInput, setMemberSearchInput] = useState('');
  const [memberSuggestions, setMemberSuggestions] = useState([]);
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const memberSearchRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when sorting/search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sortBy, sortDir]);

  // Fetch when page or other filters change
  useEffect(() => {
    fetchProjects(page, debouncedSearch, sortBy, sortDir);
  }, [page, debouncedSearch, sortBy, sortDir]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert("File size exceeds 2MB limit.");
      return;
    }

    setLoading(true);
    setShowBulkModal(false);
    try {
      const response = await importAPI.projects(file);
      const payload = response?.data?.data || {};
      const successCount = payload.success_count || 0;
      const failureCount = payload.failure_count || 0;
      const importErrors = response?.data?.errors || [];

      let message = `Bulk Import Complete!\nSuccessfully created: ${successCount}\nFailed: ${failureCount}`;
      if (importErrors.length > 0) {
        message += `\n\nSample errors:\n${importErrors.slice(0, 5).join('\n')}`;
      }

      alert(message);
      fetchProjects();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Project import failed';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async (pageNum = page, search = debouncedSearch, sort = sortBy, dir = sortDir, reset = false) => {
    if (pageNum === 1) setLoading(true);
    setError(null);
    try {
      const response = await projectsAPI.list({
          page: pageNum,
          size: 50,
          search: search || undefined,
          sort_by: sort,
          sort_dir: dir
      });
      const projectsList = response?.data?.data?.projects || response?.data?.data || response?.data || [];
      const projectsArray = Array.isArray(projectsList) ? projectsList : [];
      
      if (reset) {
          setProjects(projectsArray);
      } else {
          setProjects(prev => {
              const existingIds = new Set(prev.map(p => p.id));
              return [...prev, ...projectsArray.filter(p => !existingIds.has(p.id))];
          });
      }
      
      setHasMore(projectsArray.length === 50);
    } catch (error) {
      console.error('Error fetching projects:', error.message, error.response?.data);
      setError(`Failed to load projects: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchProjects(nextPage, debouncedSearch, sortBy, sortDir, false);
    }
  };

  // Backend handles filtering and sorting
  const filteredProjects = projects;

  const extractUsersList = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'object') {
      if (raw.users && Array.isArray(raw.users)) return raw.users;
      const vals = Object.values(raw);
      const arr = vals.find(v => Array.isArray(v) && v[0]?.id);
      if (arr) return arr;
    }
    return [];
  };

  // Fetch ALL users across pages for the allUsers list
  const fetchAllUsers = async () => {
    let page = 1;
    const pageSize = 200;
    let collected = [];
    while (true) {
      try {
        const res = await usersAPI.list({ page, size: pageSize, sort_by: 'name', sort_dir: 'asc' });
        const raw = res?.data?.data || [];
        const batch = extractUsersList(raw);
        collected = [...collected, ...batch];
        if (batch.length < pageSize) break;
        page++;
      } catch (e) {
        break;
      }
    }
    return collected;
  };

  const fetchInitialData = async () => {
    try {
      const [mgrRes, allFetched] = await Promise.all([usersAPI.getManagers(), fetchAllUsers()]);
      let managersList = mgrRes?.data?.data || [];
      setManagers(Array.isArray(managersList) ? managersList : []);
      setAllUsers(allFetched);
    } catch (error) {
      console.error('Error fetching data:', error.message, error.response?.data);
      setManagers([]);
      setAllUsers([]);
    }
  };

  // Live search for member combobox (server-side)
  const searchUsers = useCallback(async (query) => {
    if (!query || query.trim().length < 1) {
      // Show first 30 alphabetically when empty
      const sorted = [...allUsers].sort((a, b) =>
        (a.username || a.name || '').localeCompare(b.username || b.name || '')
      );
      setMemberSuggestions(sorted.slice(0, 30));
      return;
    }
    setMemberSearchLoading(true);
    try {
      const res = await usersAPI.list({ search: query.trim(), size: 30, sort_by: 'name', sort_dir: 'asc' });
      const raw = res?.data?.data || [];
      const results = extractUsersList(raw);
      setMemberSuggestions(Array.isArray(results) ? results : []);
    } catch (e) {
      // Fallback to client-side filter
      const q = query.toLowerCase();
      const filtered = allUsers.filter(u =>
        (u.username || '').toLowerCase().includes(q) ||
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      ).slice(0, 30);
      setMemberSuggestions(filtered);
    } finally {
      setMemberSearchLoading(false);
    }
  }, [allUsers]);

  const resetMemberPicker = () => {
    setMemberSearchInput('');
    setMemberSuggestions([]);
    setShowSuggestions(false);
    setHighlightedIdx(-1);
  };

  const openProjectModal = (project = null) => {
    resetMemberPicker();

    if (project) {
      setSelectedProject(project);
      setFormData({
        ...project,
        manager_ids: Array.isArray(project.manager_ids) ? project.manager_ids : [],
      });
      setModalMembers(Array.isArray(project.members) ? project.members : []);
      setIconPreview(project.icon || null);
    } else {
      setSelectedProject(null);
      setFormData({
        name: '',
        client: '',
        client_id: '',
        manager_ids: isAdmin() ? [] : [user?.id],
        reporting_area: '',
        description: '',
        icon: null,
      });
      setModalMembers([]);
      setIconPreview(null);
    }

    setShowModal(true);
  };

  const handleIconUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 100 * 1024) {
      alert('File size must be less than 100KB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result;
      setFormData(prev => ({ ...prev, icon: base64 }));
      setIconPreview(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    
    setIsSaving(true);
    const finalData = { 
      ...formData, 
      manager_ids: isAdmin() ? (formData.manager_ids || []) : [user?.id] 
    };
    try {
      if (selectedProject) { await projectsAPI.update(selectedProject.id, finalData); }
      else { await projectsAPI.create(finalData); }
      
      if (selectedProject) {
        await handleSaveProjectMembers();
      }
      
      setShowModal(false);
      setModalMembers([]);
      setPage(1);
      fetchProjects(1, debouncedSearch, sortBy, sortDir, true);
    } catch (error) {
      console.error('Error saving project:', error);
      const isTimeout = error.code === 'ECONNABORTED' || error.message.includes('timeout');
      if (isTimeout) {
          alert('Request timed out, but the project might still be created. Please refresh the list in a moment.');
          setShowModal(false);
          setPage(1);
          fetchProjects(1, debouncedSearch, sortBy, sortDir, true);
      } else {
          alert('Failed to save project');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      if (isSaving) return;
      setIsSaving(true);
      try {
        await projectsAPI.delete(projectId);
        setPage(1);
        fetchProjects(1, debouncedSearch, sortBy, sortDir, true);
      } catch (error) {
        console.error('Error deleting project:', error);
        const isTimeout = error.code === 'ECONNABORTED' || error.message.includes('timeout');
        if (isTimeout) {
            // If it timed out, it might still have succeeded in the backend
            alert('Request timed out. Refreshing list to check status...');
            setPage(1);
            fetchProjects(1, debouncedSearch, sortBy, sortDir, true);
        } else {
            alert('Failed to delete project');
        }
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleAddMemberToModal = (userId) => {
    const user = allUsers.find(u => u.id === parseInt(userId));
    if (user && !modalMembers.some(m => m.user_id === user.id)) {
      console.log('Adding member:', user);
      setModalMembers([...modalMembers, { user_id: user.id, user_name: user.name }]);
    }
  };

  const handleRemoveMemberFromModal = (userId) => {
    setModalMembers(modalMembers.filter(m => m.user_id !== userId));
  };

  const handleSaveProjectMembers = async () => {
    try {
      // Get current members from the project
      const currentMembers = selectedProject?.members || [];
      const currentMemberIds = new Set(currentMembers.map(m => m.user_id));
      const newMemberIds = new Set(modalMembers.map(m => m.user_id));
      
      if (!selectedProject?.id) {
        console.error('Cannot save members: No project selected or project has no ID');
        return;
      }

      // Add new members
      for (const memberId of newMemberIds) {
        if (memberId && !currentMemberIds.has(memberId)) {
          await projectsAPI.addMember(selectedProject.id, { user_id: memberId, bill_rate: 0 });
        }
      }
      
      // Remove members not in the list
      for (const memberId of currentMemberIds) {
        if (memberId && !newMemberIds.has(memberId)) {
          await projectsAPI.removeMember(selectedProject.id, memberId);
        }
      }
    } catch (error) {
      console.error('Error saving members:', error);
      alert('Failed to save project members');
    }
  };

  return (
    <div className="page-wrapper">
      <header className="header">
        <div className="header-title">
          <h2>Projects</h2>
          <p>Manage project assignments and client reporting</p>
        </div>
        <div className="header-actions">
          <div className="search-container">
            <Search className="search-icon" size={18} />
            <input
              type="text"
              className="form-input search-input"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="search-container" style={{ minWidth: '180px' }}>
              <select 
                  className="form-input" 
                  style={{ paddingLeft: '10px' }}
                  value={`${sortBy}-${sortDir}`}
                  onChange={(e) => {
                      const [newSortBy, newSortDir] = e.target.value.split('-');
                      setSortBy(newSortBy);
                      setSortDir(newSortDir);
                  }}
              >
                  <option value="name-asc">Alphabetical (A-Z)</option>
                  <option value="name-desc">Alphabetical (Z-A)</option>
                  <option value="created_at-desc">Date Added (Newest)</option>
                  <option value="created_at-asc">Date Added (Oldest)</option>
                  <option value="active-desc">Most Active</option>
              </select>
          </div>
          {(isAdmin() || isManager()) && (
            <>
              <button
                className="btn btn-secondary"
                onClick={() => setShowBulkModal(true)}
              >
                <Plus size={18} /> Bulk Import (CSV)
              </button>
              <button 
                className="btn btn-primary" 
                onClick={() => openProjectModal()}
              >
                <Plus size={18} /> Add Project
              </button>
            </>
          )}
        </div>
      </header>

      <div className="page-content">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        {loading ? (
          <div className="text-center py-8">Loading projects...</div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-8 text-secondary">No projects found</div>
        ) : (
          <div className="table-container" style={{ overflowX: 'auto', width: '100%' }}>
            <table className="table" style={{ width: '100%', minWidth: '800px', tableLayout: 'auto' }}>
              <thead>
                <tr>
                  <th style={{ width: '30%' }}>Project / Client</th>
                  <th style={{ width: '25%' }}>Manager</th>
                  <th style={{ width: '35%' }}>Team Allocation</th>
                  <th style={{ width: '10%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((p, index) => {
                  const isLast = index === filteredProjects.length - 1;
                  const canManage = isAdmin() || p.is_managed_by_me;
                  const projectMembers = Array.isArray(p?.members) ? p.members : [];
                  return (
                    <tr key={p.id} ref={isLast ? lastElementRef : null}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {p.icon ? (
                            <img src={p.icon} alt="" style={{ width: '36px', height: '36px', borderRadius: '8px', objectFit: 'cover', border: '1px solid var(--border)', flexShrink: 0 }} />
                          ) : (
                            <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'linear-gradient(135deg, var(--primary), var(--secondary))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '15px', fontWeight: 700, color: 'white' }}>
                              {(p.name || '?')[0].toUpperCase()}
                            </div>
                          )}
                          <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                            <button
                              type="button"
                              onClick={() => setDetailProject(p)}
                              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', width: '100%' }}
                            >
                              <div className="font-bold" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--primary)', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' }} title={p.name}>{p.name}</div>
                            </button>
                            <div className="text-xs text-secondary" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.client}</div>
                            <div className="text-xs" style={{ color: 'var(--text-muted, #94a3b8)', marginTop: '2px' }}>ID: #{p.id}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '150px' }}>{(p.managers || []).map(m => m.name).join(', ')}</td>
                      <td 
                        onMouseEnter={(e) => {
                          if (projectMembers.length > 0) {
                            setHoveredProject(p.id);
                            setBlurbPos({ x: e.clientX, y: e.clientY });
                          }
                        }}
                        onMouseLeave={() => setHoveredProject(null)}
                        style={{ position: 'relative' }}
                      >
                        <div className="flex flex-wrap gap-2 items-center">
                          {/* Show member names if: admin, manager, or employee assigned to this project */}
                          {(() => {
                            const isEmployeeOnProject = projectMembers.some(m => m.user_id === user?.id);
                            const canViewMembers = isAdmin() || isManager() || isEmployeeOnProject;
                            
                            if (canViewMembers) {
                              return (
                                <>
                                  {projectMembers.slice(0, 2).map(m => (
                                    <span
                                      key={m.user_id}
                                      className="px-2 py-1 bg-slate-100 rounded text-xs border border-slate-200"
                                      title={m.user_username ? `${m.user_name} (@${m.user_username})` : m.user_name}
                                    >
                                      {m.user_name}
                                    </span>
                                  ))}
                                  {projectMembers.length > 2 && (
                                    <span className="px-2 py-1 bg-slate-200 rounded text-xs border border-slate-300 font-semibold">+{projectMembers.length - 2}</span>
                                  )}
                                </>
                              );
                            } else {
                              // Employee not on this project - show count only
                              return (
                                <span className="text-sm text-muted">
                                  {projectMembers.length} member{projectMembers.length !== 1 ? 's' : ''}
                                </span>
                              );
                            }
                          })()}

                        </div>
                        {hoveredProject === p.id && projectMembers.length > 0 && (
                          <div 
                            style={{ 
                              position: 'fixed', 
                              left: blurbPos.x + 10, 
                              top: blurbPos.y + 10, 
                              background: 'white', 
                              border: '1px solid var(--border)', 
                              borderRadius: '8px', 
                              boxShadow: 'var(--shadow-lg)', 
                              padding: '12px', 
                              zIndex: 9999, 
                              minWidth: '200px',
                              pointerEvents: 'none'
                            }}
                          >
                            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '8px', textTransform: 'uppercase' }}>Full Team List</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {projectMembers.map(m => (
                                <div key={m.user_id} style={{ fontSize: '13px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--primary)' }}></div>
                                  <div>
                                    <div>{m.user_name}</div>
                                    {m.user_username && (
                                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>@{m.user_username}</div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {canManage && (
                            <button
                              type="button"
                              className="btn-icon"
                              onClick={() => openProjectModal(p)}
                              title="Edit project & manage team"
                            >
                              <Edit2 size={16} />
                            </button>
                          )}
                          {isAdmin() && (
                            <button className="btn-icon btn-danger" onClick={() => handleDeleteProject(p.id)} title="Delete"><Trash2 size={16} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {hasMore && (
                <div className="flex justify-center p-4">
                    <button 
                        className="btn btn-secondary" 
                        onClick={loadMore}
                        disabled={loading}
                    >
                        {loading ? 'Loading...' : 'Load More Projects'}
                    </button>
                </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Project Drawer */}
      {showModal && (
        <div className="premium-drawer-overlay" onClick={() => { setShowModal(false); setModalMembers([]); resetMemberPicker(); }}>
          <div className="premium-drawer" onClick={e => e.stopPropagation()}>
            <div className="drawer-header">
              <h3>{selectedProject ? 'Edit' : 'Create'} Project</h3>
              <button className="btn btn-icon" onClick={() => { setShowModal(false); setModalMembers([]); resetMemberPicker(); }}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
              <div className="drawer-body" style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
                <div className="form-group">
                  <label className="form-label">Project Name</label>
                  <input 
                    className="form-input" 
                    placeholder="e.g. Q4 Audit" 
                    value={formData.name} 
                    onChange={e => setFormData({ ...formData, name: e.target.value })} 
                    required 
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Client / Department</label>
                  <input 
                    className="form-input" 
                    placeholder="e.g. Internal" 
                    value={formData.client} 
                    onChange={e => setFormData({ ...formData, client: e.target.value })} 
                    required 
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Project Managers</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '180px', overflowY: 'auto', padding: '10px', border: '1px solid var(--border)', borderRadius: '8px' }}>
                    {(Array.isArray(managers) ? managers : []).map(m => (
                      <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={(formData.manager_ids || []).includes(m.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, manager_ids: [...(formData.manager_ids || []), m.id] });
                            } else {
                              setFormData({ ...formData, manager_ids: (formData.manager_ids || []).filter(id => id !== m.id) });
                            }
                          }}
                          disabled={!isAdmin()}
                          style={{ width: '16px', height: '16px', margin: 0, cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>{m.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Project Icon (Image)</label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleIconUpload}
                        style={{ display: 'block', marginBottom: '8px' }}
                      />
                      <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Max size: 100KB. Recommended: square images</p>
                    </div>
                    {(iconPreview || formData.icon) && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                        <img 
                          src={iconPreview || formData.icon} 
                          alt="Icon preview" 
                          style={{ width: '80px', height: '80px', borderRadius: '8px', objectFit: 'cover', border: '1px solid var(--border)' }}
                        />
                        <button 
                          type="button" 
                          className="btn btn-secondary" 
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                          onClick={() => { setFormData(prev => ({ ...prev, icon: null })); setIconPreview(null); }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Reporting Area</label>
                  <input 
                    className="form-input" 
                    placeholder="e.g. Audit" 
                    value={formData.reporting_area || ''} 
                    onChange={e => setFormData({ ...formData, reporting_area: e.target.value })} 
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea 
                    className="form-input" 
                    rows="3"
                    value={formData.description || ''} 
                    onChange={e => setFormData({ ...formData, description: e.target.value })} 
                    placeholder="Enter project description..."
                  />
                </div>
                
                {selectedProject && (
                  <div style={{ marginTop: '32px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                      <label style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' }}>Team Members</label>
                      <span style={{ fontSize: '12px', background: 'rgba(13, 148, 136, 0.1)', color: 'var(--primary)', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
                        {modalMembers.length} Assigned
                      </span>
                    </div>
                    
                    {/* Current Members */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                      {modalMembers.map(m => (
                        <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid var(--border)' }}>
                          <span style={{ fontSize: '14px', fontWeight: 500 }}>{m.user_name}</span>
                          <button
                            type="button"
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}
                            onClick={() => handleRemoveMemberFromModal(m.user_id)}
                            title="Remove member"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                      {modalMembers.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '20px', background: '#f8fafc', borderRadius: '10px', border: '1px dashed var(--border)', color: 'var(--text-secondary)', fontSize: '13px' }}>
                          No members assigned yet
                        </div>
                      )}
                    </div>
                    
                    {/* Add Members — Searchable Combobox */}
                    <div className="form-group" style={{ position: 'relative' }}>
                      <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px', display: 'block' }}>
                        Add Members
                      </label>
                      {/* Help text */}
                      <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '8px', lineHeight: 1.4 }}>
                        Search by username / name / email. You can also type multiple usernames or emails
                        separated by commas and press <kbd style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '3px', padding: '0 4px', fontSize: '10px' }}>Enter</kbd>.
                      </p>
                      <div style={{ position: 'relative' }}>
                        <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', pointerEvents: 'none' }} />
                        <input
                          ref={memberSearchRef}
                          type="text"
                          className="form-input"
                          placeholder="username, email, or name..."
                          value={memberSearchInput}
                          style={{ paddingLeft: '32px', paddingRight: '36px' }}
                          onChange={(e) => {
                            const val = e.target.value;
                            setMemberSearchInput(val);
                            setHighlightedIdx(-1);
                            // Get the last segment after last comma for live search
                            const parts = val.split(',');
                            const lastPart = parts[parts.length - 1].trim();
                            searchUsers(lastPart);
                            setShowSuggestions(true);
                          }}
                          onFocus={() => {
                            const parts = memberSearchInput.split(',');
                            const lastPart = parts[parts.length - 1].trim();
                            searchUsers(lastPart);
                            setShowSuggestions(true);
                          }}
                          onBlur={() => setTimeout(() => setShowSuggestions(false), 180)}
                          onKeyDown={(e) => {
                            if (!showSuggestions) return;
                            const visible = memberSuggestions.filter(u => !modalMembers.some(m => m.user_id === u.id));
                            if (e.key === 'ArrowDown') {
                              e.preventDefault();
                              setHighlightedIdx(i => Math.min(i + 1, visible.length - 1));
                            } else if (e.key === 'ArrowUp') {
                              e.preventDefault();
                              setHighlightedIdx(i => Math.max(i - 1, 0));
                            } else if (e.key === 'Enter') {
                              e.preventDefault();
                              if (highlightedIdx >= 0 && visible[highlightedIdx]) {
                                // Pick highlighted suggestion
                                handleAddMemberToModal(String(visible[highlightedIdx].id));
                                setMemberSearchInput('');
                                setShowSuggestions(false);
                                setHighlightedIdx(-1);
                              } else {
                                // Parse comma-separated input
                                const parts = memberSearchInput.split(',').map(p => p.trim()).filter(Boolean);
                                let added = 0;
                                parts.forEach(part => {
                                  const match = allUsers.find(u =>
                                    (u.username || '').toLowerCase() === part.toLowerCase() ||
                                    (u.email || '').toLowerCase() === part.toLowerCase() ||
                                    (u.name || '').toLowerCase() === part.toLowerCase()
                                  );
                                  if (match) { handleAddMemberToModal(String(match.id)); added++; }
                                });
                                if (added > 0) {
                                  setMemberSearchInput('');
                                  setShowSuggestions(false);
                                }
                              }
                            } else if (e.key === 'Escape') {
                              setShowSuggestions(false);
                            }
                          }}
                        />
                        {memberSearchInput && (
                          <button
                            type="button"
                            onClick={() => { setMemberSearchInput(''); setShowSuggestions(false); }}
                            style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px', lineHeight: 1 }}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>

                      {/* Suggestions dropdown */}
                      {showSuggestions && (() => {
                        const visible = memberSuggestions.filter(u => !modalMembers.some(m => m.user_id === u.id));
                        return visible.length > 0 ? (
                          <div
                            ref={suggestionsRef}
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              background: 'var(--bg-card, white)',
                              border: '1px solid var(--border)',
                              borderRadius: '10px',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                              zIndex: 1000,
                              maxHeight: '220px',
                              overflowY: 'auto',
                              marginTop: '4px',
                            }}
                          >
                            {memberSearchLoading && (
                              <div style={{ padding: '8px 14px', fontSize: '12px', color: 'var(--text-secondary)' }}>Searching...</div>
                            )}
                            {visible.map((u, idx) => (
                              <div
                                key={u.id}
                                onMouseDown={() => {
                                  handleAddMemberToModal(String(u.id));
                                  setMemberSearchInput('');
                                  setShowSuggestions(false);
                                  setHighlightedIdx(-1);
                                }}
                                style={{
                                  padding: '9px 14px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  background: idx === highlightedIdx ? 'rgba(13,148,136,0.08)' : 'transparent',
                                  borderBottom: idx < visible.length - 1 ? '1px solid var(--border)' : 'none',
                                  transition: 'background 0.1s',
                                }}
                                onMouseEnter={() => setHighlightedIdx(idx)}
                              >
                                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--primary), var(--secondary))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: 'white', flexShrink: 0 }}>
                                  {(u.name || u.username || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {u.username || u.name}
                                  </div>
                                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {u.name}{u.email ? ` · ${u.email}` : ''}
                                  </div>
                                </div>
                                <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
                                  <UserPlus size={14} style={{ color: 'var(--primary)' }} />
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : memberSearchInput.trim() && !memberSearchLoading ? (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-card, white)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 14px', marginTop: '4px', fontSize: '13px', color: 'var(--text-secondary)', zIndex: 1000 }}>
                            No users found for &ldquo;{memberSearchInput}&rdquo;
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="drawer-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setModalMembers([]); resetMemberPicker(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSaving}>
                  {isSaving ? 'Saving...' : (selectedProject ? 'Save Changes' : 'Create Project')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Import Project Drawer */}
      {showBulkModal && (
        <div className="premium-drawer-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="premium-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3>Bulk Import Projects</h3>
              <button className="btn btn-icon" onClick={() => setShowBulkModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="drawer-body">
              <div style={{ marginBottom: '24px', padding: '20px', backgroundColor: 'rgba(8, 145, 178, 0.05)', borderRadius: '12px', border: '1px solid rgba(8, 145, 178, 0.1)' }}>
                <p style={{ fontWeight: 700, marginBottom: '12px', color: 'var(--secondary)', fontSize: '15px' }}>CSV Requirements</p>
                <ul style={{ fontSize: '13px', color: 'var(--text-secondary)', paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <li><b>name</b> column is <b>mandatory</b></li>
                  <li>Optional: <b>client, reporting_area, manager_id, manager_email, manager_username, manager_name, status</b></li>
                  <li>Default values: <b>client = Internal</b>, <b>manager = Current User</b></li>
                  <li>File size limit: <b>2MB</b></li>
                </ul>
              </div>
              
              <div 
                style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  padding: '40px 20px', 
                  border: '2px dashed var(--border)', 
                  borderRadius: '16px', 
                  textAlign: 'center',
                  background: '#f8fafc',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onClick={() => document.getElementById('project-csv-import-hidden').click()}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--secondary)'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', marginBottom: '20px' }}>
                   <FolderPlus size={32} style={{ color: 'var(--secondary)' }} />
                </div>
                <h4 style={{ fontWeight: 700, marginBottom: '8px' }}>Upload Project List</h4>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', maxWidth: '240px' }}>
                  Choose a CSV file to create multiple projects at once.
                </p>
                <input 
                  type="file" 
                  id="project-csv-import-hidden" 
                  hidden 
                  accept=".csv" 
                  onChange={handleFileChange} 
                />
              </div>
            </div>
            
            <div className="drawer-footer">
              <button className="btn btn-secondary" style={{ width: '100%' }} onClick={() => setShowBulkModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Detail Drawer */}
      {detailProject && (
        <div className="premium-drawer-overlay" onClick={() => setDetailProject(null)}>
          <div className="premium-drawer" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="drawer-header" style={{ paddingBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {detailProject.icon ? (
                  <img src={detailProject.icon} alt="" style={{ width: '48px', height: '48px', borderRadius: '12px', objectFit: 'cover', border: '1px solid var(--border)' }} />
                ) : (
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'linear-gradient(135deg, var(--primary), var(--secondary))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 700, color: 'white' }}>
                    {(detailProject.name || '?')[0].toUpperCase()}
                  </div>
                )}
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{detailProject.name}</h3>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{detailProject.client}</span>
                </div>
              </div>
              <button className="btn btn-icon" onClick={() => setDetailProject(null)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="drawer-body" style={{ display: 'flex', flexDirection: 'column', gap: '24px', overflowY: 'auto' }}>
              {/* Project ID & Status Badges */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '6px', background: 'var(--border)', fontWeight: 600 }}>ID: #{detailProject.id}</span>
                <span style={{ 
                  fontSize: '12px', 
                  padding: '4px 8px', 
                  borderRadius: '6px', 
                  background: detailProject.status ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', 
                  color: detailProject.status ? 'var(--success)' : 'var(--danger)', 
                  fontWeight: 700 
                }}>
                  {detailProject.status ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* Description */}
              <div>
                <h4 style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '8px', letterSpacing: '0.5px' }}>Description</h4>
                <p style={{ fontSize: '14px', lineHeight: '1.6', margin: 0, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                  {detailProject.description || 'No description provided.'}
                </p>
              </div>

              {/* Parameters */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Reporting Area</span>
                  <span style={{ fontSize: '14px', fontWeight: 600 }}>{detailProject.reporting_area || 'None'}</span>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Status</span>
                  <span style={{ fontSize: '14px', fontWeight: 600 }}>{detailProject.status ? 'Active' : 'Inactive'}</span>
                </div>
              </div>

              {/* Managers */}
              <div>
                <h4 style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '8px', letterSpacing: '0.5px' }}>Project Manager(s)</h4>
                {detailProject.managers && detailProject.managers.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {detailProject.managers.map(mgr => (
                      <div key={mgr.id} style={{ fontSize: '14px', display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 600 }}>{mgr.name}</span>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{mgr.email}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>No managers assigned.</span>
                )}
              </div>

              {/* Members List */}
              <div>
                <h4 style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '8px', letterSpacing: '0.5px' }}>Team Allocation</h4>
                {detailProject.members && detailProject.members.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '180px', overflowY: 'auto', paddingRight: '4px' }}>
                    {detailProject.members.map(member => (
                      <div key={member.user_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'white', border: '1px solid var(--border)', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontSize: '13px', fontWeight: 600 }}>{member.user_name}</span>
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{member.user_email}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>No team members assigned.</span>
                )}
              </div>
            </div>
            
            <div className="drawer-footer">
              <button className="btn btn-secondary" style={{ width: '100%' }} onClick={() => setDetailProject(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Projects;




